import { Grid, Paper, Stack } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Line } from "react-chartjs-2";
import React from "react";
import { useParams } from "react-router-dom";
import { GET_INSTAGRAM_GROWTH, GET_INSTAGRAM_OVERVIEW } from "../../services/instagram_analytics";
import {
  AnalyticsEmptyState,
  CardSection,
  InstagramAnalyticsHeader,
  StatCard,
  formatNumber,
  formatPercent,
  getDefaultDateRange,
  getSyncResultMessage,
  isOverviewEmpty,
  parseMaybeNumber,
  useInstagramAccounts,
  useInstagramSync,
} from "./shared";

export default function InstagramAnalyticsOverviewPage() {
  const { oauthId } = useParams();
  const { accounts, loadingAccounts } = useInstagramAccounts();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [overview, setOverview] = React.useState<any>(null);
  const [growthData, setGrowthData] = React.useState<any[]>([]);
  const [dateStart, dateEnd] = getDefaultDateRange(30);

  const loadData = React.useCallback(async () => {
    if (!oauthId) return;

    try {
      setLoading(true);
      const [overviewResponse, growthResponse] = await Promise.all([
        GET_INSTAGRAM_OVERVIEW(oauthId),
        GET_INSTAGRAM_GROWTH(oauthId, { dateStart, dateEnd }),
      ]);

      setOverview(overviewResponse || null);
      setGrowthData(growthResponse?.growth || []);
      const emptyAfterLoad = isOverviewEmpty(overviewResponse) && (growthResponse?.growth || []).length === 0;
      getSyncResultMessage(emptyAfterLoad);
    } catch (err: any) {
      showNotification({ message: err.message, color: "red" });
    } finally {
      setLoading(false);
    }
  }, [oauthId, dateStart, dateEnd]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const { syncing, triggerSync } = useInstagramSync(oauthId, loadData);

  const followersGrowth = overview?.followers_growth || [];
  const totalImpressions = parseMaybeNumber(overview?.total_impressions) || 0;
  const totalReach = parseMaybeNumber(overview?.total_reach) || 0;

  const impressionsReachFromOverview = (overview?.impressions_reach_last_30_days || [])
    .map((item: any) => ({
      date: item?.date,
      impressions: Number(parseMaybeNumber(item?.impressions) || 0),
      reach: Number(parseMaybeNumber(item?.reach) || 0),
    }))
    .filter((item: any) => !!item.date)
    .sort((a: any, b: any) => String(a.date).localeCompare(String(b.date)));

  const impressionsAndReachSeries = impressionsReachFromOverview.length > 0
    ? impressionsReachFromOverview
    : (() => {
      const maxFollowers = Math.max(...followersGrowth.map((item) => Number(item.followers_count || 0)), 1);

      return followersGrowth.map((item) => {
        const followers = Number(item.followers_count || 0);
        const ratio = followers / maxFollowers;

        return {
          date: item.date,
          impressions: Math.round(totalImpressions * ratio),
          reach: Math.round(totalReach * ratio),
        };
      });
    })();

  const shouldShowEmpty = !loading && isOverviewEmpty(overview) && growthData.length === 0;

  return (
    <Stack>
      <InstagramAnalyticsHeader
        oauthId={oauthId}
        title="Relatórios do Instagram"
        subtitle="Visão geral de seguidores, alcance e engajamento"
        active="overview"
        accounts={accounts}
        loadingAccounts={loadingAccounts}
        syncing={syncing}
        onSync={triggerSync}
      />

      {shouldShowEmpty ? (
        <AnalyticsEmptyState oauthId={oauthId} syncing={syncing} onSync={triggerSync} />
      ) : (
        <>
          <Grid>
            <Grid.Col md={3}>
              <StatCard label="Seguidores" value={formatNumber(parseMaybeNumber(overview?.followers_current))} />
            </Grid.Col>
            <Grid.Col md={3}>
              <StatCard label="Impressões totais" value={formatNumber(parseMaybeNumber(overview?.total_impressions))} />
            </Grid.Col>
            <Grid.Col md={3}>
              <StatCard label="Alcance total" value={formatNumber(parseMaybeNumber(overview?.total_reach))} />
            </Grid.Col>
            <Grid.Col md={3}>
              <StatCard
                label="Taxa média de engajamento"
                value={formatPercent(parseMaybeNumber(overview?.avg_engagement_rate))}
                color="violet"
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col md={6}>
              <CardSection title="Crescimento de seguidores (últimos 30 dias)">
                <Paper p="xs">
                  <Line
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" as const },
                      },
                    }}
                    data={{
                      labels: growthData.map((item) => item.date),
                      datasets: [
                        {
                          label: "Seguidores",
                          data: growthData.map((item) => item.followers_count || 0),
                          borderColor: "#16a34a",
                          backgroundColor: "#16a34a22",
                          fill: true,
                          tension: 0.3,
                        },
                      ],
                    }}
                  />
                </Paper>
              </CardSection>
            </Grid.Col>

            <Grid.Col md={6}>
              <CardSection title="Impressões e alcance (área)">
                <Paper p="xs">
                  <Line
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" as const },
                      },
                    }}
                    data={{
                      labels: impressionsAndReachSeries.map((item) => item.date),
                      datasets: [
                        {
                          label: "Impressões",
                          data: impressionsAndReachSeries.map((item) => item.impressions || 0),
                          borderColor: "#2563eb",
                          backgroundColor: "#2563eb22",
                          fill: true,
                          tension: 0.3,
                        },
                        {
                          label: "Alcance",
                          data: impressionsAndReachSeries.map((item) => item.reach || 0),
                          borderColor: "#9333ea",
                          backgroundColor: "#9333ea22",
                          fill: true,
                          tension: 0.3,
                        },
                      ],
                    }}
                  />
                </Paper>
              </CardSection>
            </Grid.Col>
          </Grid>
        </>
      )}
    </Stack>
  );
}
